import { parse, isValid } from 'date-fns'
import momentGuess from 'moment-guess'

const guessFormat = momentGuess as unknown as (
    date: string,
    format?: 'default' | 'strftime',
) => string | string[]

export interface DateContext {
    country?: string
    currency?: string
    formatHint?: string
}

/**
 * Maps month names in various languages to English short month names.
 * Supports German, French, Spanish, Italian, Dutch, Swedish, Polish, Czech, Mandarin, Portuguese.
 */
const MONTH_NAME_MAP: Record<string, string> = {
    // German
    januar: 'Jan',
    februar: 'Feb',
    märz: 'Mar',
    maerz: 'Mar',
    april: 'Apr',
    mai: 'May',
    juni: 'Jun',
    juli: 'Jul',
    august: 'Aug',
    september: 'Sep',
    oktober: 'Oct',
    november: 'Nov',
    dezember: 'Dec',
    mär: 'Mar',
    okt: 'Oct',
    dez: 'Dec',

    // French
    janvier: 'Jan',
    février: 'Feb',
    fevrier: 'Feb',
    mars: 'Mar',
    avril: 'Apr',
    juin: 'Jun',
    juillet: 'Jul',
    aout: 'Aug',
    août: 'Aug',
    septembre: 'Sep',
    octobre: 'Oct',
    novembre: 'Nov',
    décembre: 'Dec',
    decembre: 'Dec',
    janv: 'Jan',
    févr: 'Feb',
    fevr: 'Feb',
    avr: 'Apr',
    juil: 'Jul',
    sept: 'Sep',
    déc: 'Dec',

    // Spanish
    enero: 'Jan',
    febrero: 'Feb',
    marzo: 'Mar',
    abril: 'Apr',
    mayo: 'May',
    junio: 'Jun',
    julio: 'Jul',
    agosto: 'Aug',
    septiembre: 'Sep',
    octubre: 'Oct',
    noviembre: 'Nov',
    diciembre: 'Dec',
    ene: 'Jan',
    abr: 'Apr',
    dic: 'Dec',

    // Italian
    gennaio: 'Jan',
    febbraio: 'Feb',
    aprile: 'Apr',
    maggio: 'May',
    giugno: 'Jun',
    luglio: 'Jul',
    settembre: 'Sep',
    ottobre: 'Oct',
    dicembre: 'Dec',
    gen: 'Jan',
    mag: 'May',
    giu: 'Jun',
    lug: 'Jul',
    ott: 'Oct',

    // Dutch (november and december are same as German, so not duplicated)
    januari: 'Jan',
    februari: 'Feb',
    maart: 'Mar',
    mei: 'May',
    augustus: 'Aug',
    mrt: 'Mar',

    // Swedish (januari and februari are same as Dutch, so not duplicated)
    maj: 'May',
    augusti: 'Aug',

    // Polish
    styczeń: 'Jan',
    stycznia: 'Jan',
    luty: 'Feb',
    lutego: 'Feb',
    marzec: 'Mar',
    marca: 'Mar',
    kwiecień: 'Apr',
    kwietnia: 'Apr',
    maja: 'May',
    czerwiec: 'Jun',
    czerwca: 'Jun',
    lipiec: 'Jul',
    lipca: 'Jul',
    sierpień: 'Aug',
    sierpnia: 'Aug',
    wrzesień: 'Sep',
    września: 'Sep',
    październik: 'Oct',
    pazdziernik: 'Oct',
    października: 'Oct',
    pazdziernika: 'Oct',
    listopad: 'Nov',
    listopada: 'Nov',
    grudzień: 'Dec',
    grudnia: 'Dec',
    sty: 'Jan',
    lut: 'Feb',
    kwi: 'Apr',
    cze: 'Jun',
    lip: 'Jul',
    sie: 'Aug',
    wrz: 'Sep',
    paź: 'Oct',
    paz: 'Oct',
    gru: 'Dec',

    // Czech
    leden: 'Jan',
    únor: 'Feb',
    unor: 'Feb',
    březen: 'Mar',
    brezen: 'Mar',
    duben: 'Apr',
    květen: 'May',
    kveten: 'May',
    červen: 'Jun',
    cerven: 'Jun',
    červenec: 'Jul',
    cervenec: 'Jul',
    srpen: 'Aug',
    září: 'Sep',
    zari: 'Sep',
    říjen: 'Oct',
    rijen: 'Oct',
    prosinec: 'Dec',
    led: 'Jan',
    úno: 'Feb',
    uno: 'Feb',
    bře: 'Mar',
    bre: 'Mar',
    dub: 'Apr',
    kvě: 'May',
    kve: 'May',
    čvn: 'Jun',
    cvn: 'Jun',
    čvc: 'Jul',
    cvc: 'Jul',
    srp: 'Aug',
    zář: 'Sep',
    zar: 'Sep',
    říj: 'Oct',
    rij: 'Oct',
    pro: 'Dec',

    // Portuguese
    janeiro: 'Jan',
    fevereiro: 'Feb',
    março: 'Mar',
    marco: 'Mar',
    maio: 'May',
    junho: 'Jun',
    julho: 'Jul',
    setembro: 'Sep',
    outubro: 'Oct',
    novembro: 'Nov',
    dezembro: 'Dec',
    fev: 'Feb',
    out: 'Oct',

    // Common abbreviations (shared across languages)
    jan: 'Jan',
    feb: 'Feb',
    mar: 'Mar',
    apr: 'Apr',
    may: 'May',
    jun: 'Jun',
    jul: 'Jul',
    aug: 'Aug',
    sep: 'Sep',
    oct: 'Oct',
    nov: 'Nov',
    dec: 'Dec',
    lis: 'Nov',

    // Mandarin (simplified Chinese - using pinyin)
    yiyue: 'Jan',
    eryue: 'Feb',
    sanyue: 'Mar',
    siyue: 'Apr',
    wuyue: 'May',
    liuyue: 'Jun',
    qiyue: 'Jul',
    bayue: 'Aug',
    jiuyue: 'Sep',
    shiyue: 'Oct',
    shiyiyue: 'Nov',
    shieryue: 'Dec',
    // Mandarin numbers (一月 = January, etc.)
    一月: 'Jan',
    二月: 'Feb',
    三月: 'Mar',
    四月: 'Apr',
    五月: 'May',
    六月: 'Jun',
    七月: 'Jul',
    八月: 'Aug',
    九月: 'Sep',
    十月: 'Oct',
    十一月: 'Nov',
    十二月: 'Dec',
}

/**
 * Normalizes month names in the date string to English short versions.
 */
function normalizeMonthNames(dateString: string): string {
    let normalized = dateString

    // Create a case-insensitive regex replacement
    for (const [foreignMonth, englishMonth] of Object.entries(MONTH_NAME_MAP)) {
        // Match whole word boundaries to avoid partial replacements
        const regex = new RegExp(`\\b${foreignMonth}\\b`, 'gi')
        normalized = normalized.replace(regex, englishMonth)
    }

    return normalized
}

/**
 * Converts moment.js format string to date-fns format string.
 * Handles common format tokens.
 */
function convertMomentFormatToDateFns(momentFormat: string): string {
    // Map moment.js tokens to date-fns tokens
    const tokenMap: Record<string, string> = {
        YYYY: 'yyyy',
        YY: 'yy',
        MM: 'MM',
        M: 'M',
        DD: 'dd',
        D: 'd',
        HH: 'HH',
        H: 'H',
        hh: 'hh',
        h: 'h',
        mm: 'mm',
        m: 'm',
        ss: 'ss',
        s: 's',
        A: 'a',
        a: 'a',
        z: 'XXX', // timezone offset
        Z: 'XXX',
        MMM: 'MMM', // Short month name (Jan, Feb, etc.)
        MMMM: 'MMMM', // Full month name
        ddd: 'EEE', // Short day name
        dddd: 'EEEE', // Full day name
        Do: 'do', // Ordinal day
    }

    let dateFnsFormat = momentFormat

    // Replace tokens (longer tokens first to avoid partial matches)
    const sortedTokens = Object.keys(tokenMap).sort(
        (a, b) => b.length - a.length,
    )

    for (const momentToken of sortedTokens) {
        const dateFnsToken = tokenMap[momentToken]
        // Use word boundaries for letter tokens to avoid partial matches
        if (/^[A-Za-z]+$/.test(momentToken)) {
            const regex = new RegExp(`\\b${momentToken}\\b`, 'g')
            dateFnsFormat = dateFnsFormat.replace(regex, dateFnsToken)
        } else {
            dateFnsFormat = dateFnsFormat.replace(
                new RegExp(
                    momentToken.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
                    'g',
                ),
                dateFnsToken,
            )
        }
    }

    return dateFnsFormat
}

/**
 * Infers date order (MDY/DMY/YMD) from context.
 */
function inferDateOrder(context?: DateContext): 'MDY' | 'DMY' | 'YMD' {
    if (context?.country === 'US' || context?.currency === 'USD') {
        return 'MDY'
    }
    // Most European countries use DMY
    if (
        context?.country === 'DE' ||
        context?.country === 'FR' ||
        context?.country === 'ES' ||
        context?.country === 'IT' ||
        context?.country === 'NL' ||
        context?.country === 'PL' ||
        context?.country === 'CZ' ||
        context?.currency === 'EUR'
    ) {
        return 'DMY'
    }
    // Some Asian countries use YMD
    if (
        context?.country === 'CN' ||
        context?.country === 'JP' ||
        context?.country === 'KR'
    ) {
        return 'YMD'
    }
    // Default to DMY (European standard)
    return 'DMY'
}

/**
 * Selects the best format from ambiguous results based on context.
 */
function selectBestFormat(
    formats: string[],
    dateOrder: 'MDY' | 'DMY' | 'YMD',
): string {
    if (dateOrder === 'DMY') {
        const dmyFormat = formats.find(
            (f) =>
                f.includes('DD/MM') ||
                f.includes('DD-MM') ||
                f.includes('DD.MM') ||
                f.includes('D/MM') ||
                f.includes('D-MM') ||
                f.includes('D.MM') ||
                f.includes('DD MMM') ||
                f.includes('D MMM'),
        )
        if (dmyFormat) return dmyFormat
    } else if (dateOrder === 'MDY') {
        const mdyFormat = formats.find(
            (f) =>
                f.includes('MM/DD') ||
                f.includes('MM-DD') ||
                f.includes('MM.DD') ||
                f.includes('MM/D') ||
                f.includes('MM-D') ||
                f.includes('MM.D') ||
                f.includes('MMM DD') ||
                f.includes('MMM D'),
        )
        if (mdyFormat) return mdyFormat
    } else if (dateOrder === 'YMD') {
        const ymdFormat = formats.find(
            (f) =>
                f.includes('YYYY/MM') ||
                f.includes('YYYY-MM') ||
                f.includes('YYYY.MM') ||
                f.includes('YYYY MMM'),
        )
        if (ymdFormat) return ymdFormat
    }

    return formats[0]
}

/**
 * Validates that a date is within reasonable bounds.
 */
function isReasonableDate(date: Date): boolean {
    const year = date.getUTCFullYear()
    return year >= 1900 && year <= 2100
}

/**
 * Normalizes a free-text date string to a Unix timestamp in GMT+0 (UTC).
 * Returns null if the date cannot be parsed.
 *
 * @param dateString - The date string to normalize
 * @param context - Optional context with country code and currency to help disambiguate formats
 * @returns Unix timestamp in milliseconds (number) or null if parsing fails
 */
export function normalizeDateFromFreeText(
    dateString: string | null,
    context?: DateContext,
): number | null {
    if (!dateString || typeof dateString !== 'string') {
        return null
    }

    let trimmed = dateString.trim()

    if (!trimmed) {
        return null
    }

    // Step 1: Normalize month names to English short versions
    trimmed = normalizeMonthNames(trimmed)

    // Strip ordinal suffixes (e.g., "29th" -> "29", "1st" -> "1")
    trimmed = trimmed.replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1')

    // Step 2: Use moment-guess to detect the date format
    try {
        const guessedFormats = guessFormat(trimmed)
        const formats = Array.isArray(guessedFormats)
            ? guessedFormats
            : [guessedFormats]

        if (formats.length === 0) {
            return null
        }

        // Step 3: If ambiguous, use context to select the best format
        const dateOrder = inferDateOrder(context)
        let selectedFormat =
            formats.length > 1
                ? selectBestFormat(formats, dateOrder)
                : formats[0]

        if (context?.formatHint && formats.includes(context.formatHint)) {
            selectedFormat = context.formatHint
        }

        // Step 4: Convert moment format to date-fns format and parse
        const dateFnsFormat = convertMomentFormatToDateFns(selectedFormat)

        try {
            // Parse the date using date-fns
            // Use a reference date in UTC to avoid timezone issues
            const referenceDate = new Date('2000-01-01T00:00:00Z')
            const parsedDate = parse(trimmed, dateFnsFormat, referenceDate)

            if (isValid(parsedDate) && isReasonableDate(parsedDate)) {
                // Extract date components from the parsed date
                // Note: parse returns a Date in local time, so we need to extract components carefully
                const year = parsedDate.getFullYear()
                const month = parsedDate.getMonth()
                const day = parsedDate.getDate()
                const hours = parsedDate.getHours()
                const minutes = parsedDate.getMinutes()
                const seconds = parsedDate.getSeconds()

                // Create UTC timestamp from the extracted components
                const unixTimestamp = Date.UTC(
                    year,
                    month,
                    day,
                    hours,
                    minutes,
                    seconds,
                )
                return unixTimestamp
            }
        } catch (parseError) {
            // If date-fns parse fails, try fallback parsing
        }

        // Fallback: Try parsing with native Date if format conversion failed
        try {
            const nativeDate = new Date(trimmed + ' UTC')
            if (!isNaN(nativeDate.getTime()) && isReasonableDate(nativeDate)) {
                const unixTimestamp = nativeDate.getTime()
                return unixTimestamp
            }
        } catch (error) {
            // Ignore parsing errors
        }
    } catch (error) {
        // If moment-guess fails, try native Date parsing as fallback
        try {
            const nativeDate = new Date(trimmed + ' UTC')
            if (!isNaN(nativeDate.getTime()) && isReasonableDate(nativeDate)) {
                const unixTimestamp = nativeDate.getTime()
                return unixTimestamp
            }
        } catch (parseError) {
            // Ignore parsing errors
        }
    }

    return null
}
